import React from "react";
import { Dialog } from "@mui/material";
import { user } from "server/AccrossTypes";
import { userRoleDisplay } from "components/common/utils";

interface UserDetailsModalProps {
  user: user | null;
  open: boolean;
  onClose: () => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  user,
  open,
  onClose,
}) => {
  if (!user) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Non spécifié";

    try {
      return new Date(dateString).toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Date invalide";
    }
  };

  const formatSchoolClass = (schoolClass?: string) => {
    if (!schoolClass) return "Non spécifié";

    const classMap: { [key: string]: string } = {
      terminale_A1: "Terminale A1",
      terminale_A2: "Terminale A2",
      terminale_B: "Terminale B",
      terminale_C: "Terminale C",
      terminale_D: "Terminale D",
      terminale_G1: "Terminale G1",
      terminale_G2: "Terminale G2",
      terminale_G3: "Terminale G3",
      troisieme_mc: "Troisième MC",
      troisieme_ml: "Troisième ML",
    };

    return classMap[schoolClass] || schoolClass;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 9999 }}
    >
      {/* Header */}
      <div className="relative p-4 md:p-6 bg-[#131CC9] text-white flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3 md:space-x-4">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center text-[#131CC9] font-bold text-sm md:text-base">
            {user.firstname && user.lastname
              ? user.firstname[0] + user.lastname[0]
              : ""}
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold">
              {user.firstname} {user.lastname}
            </h2>
            <p className="text-blue-100 text-sm md:text-base">
              {userRoleDisplay(user.userRole)}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-blue-200 transition-colors p-1"
        >
          <svg
            className="w-5 h-5 md:w-6 md:h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Contenu */}
      <div className="p-4 md:p-6">
        {/* User Information */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
            <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-4">
              Informations de base
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Nom complet
                </label>
                <p className="text-gray-800 font-medium">
                  {user.firstname} {user.lastname}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Rôle
                </label>
                <p className="text-gray-800 font-medium">
                  {userRoleDisplay(user.userRole)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  ID utilisateur
                </label>
                <p className="text-gray-800 font-mono text-sm">{user._id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Date de création
                </label>
                <p className="text-gray-800">{formatDate(user.createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Informations de contact
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Email
                </label>
                <p className="text-gray-800">{user.email || "Non spécifié"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Téléphone
                </label>
                <p className="text-gray-800">
                  {user.phoneNumber || "Non spécifié"}
                </p>
              </div>
            </div>
          </div>

          {/* School Information */}
          {(user.userRole === "student" ||
            user.userRole === "teacher" ||
            user.userRole === "admin_school") && (
            <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
              <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-4">
                Informations scolaires
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    École
                  </label>
                  <p className="text-gray-800">
                    {user.schoolName || user.school || "Non spécifié"}
                  </p>
                </div>
                {user.userRole === "student" && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Classe
                    </label>
                    <p className="text-gray-800">
                      {formatSchoolClass(user.schoolClass)}
                    </p>
                  </div>
                )}
                {user.userRole === "student" && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Matricule
                    </label>
                    <p className="text-gray-800">
                      {user.matricule || "Non spécifié"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Teacher Specific Information */}
          {user.userRole === "teacher" && (
            <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
              <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-4">
                Disponibilité
              </h3>
              <div className="flex items-center space-x-2">
                <div
                  className={`w-3 h-3 rounded-full ${
                    user.availability.available ? "bg-green-500" : "bg-red-500"
                  }`}
                ></div>
                <span className="text-gray-800">
                  {user.availability.available
                    ? "Disponible"
                    : "Non disponible"}
                </span>
              </div>
              {!user.availability.available && user.availability.reason && (
                <p className="text-gray-600 text-sm mt-2">
                  Raison: {user.availability.reason}
                </p>
              )}
            </div>
          )}

          {/* Parent Specific Information */}
          {user.userRole === "parent" &&
            user.childrenId &&
            user.childrenId.length > 0 && (
              <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-4">
                  Enfants associés
                </h3>
                <div className="space-y-2">
                  {user.childrenId.map((childId, index) => (
                    <div key={childId} className="flex items-center space-x-2">
                      <span className="text-gray-600">{index + 1}.</span>
                      <span className="text-gray-800 font-mono text-sm">
                        {childId}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 md:p-4 bg-gray-50 border-t flex justify-end">
        <button
          onClick={onClose}
          className="px-3 md:px-4 py-2 bg-[#131CC9] text-white rounded-lg hover:bg-[#0f1699] transition-colors text-sm md:text-base"
        >
          Fermer
        </button>
      </div>
    </Dialog>
  );
};

export default UserDetailsModal;
